import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Navigation, useAuth } from '../App';
import { 
  BookOpen, ChevronRight, Target, TrendingUp, Award, Clock, 
  CheckCircle, ArrowRight, Play, Lock, Star, Zap, Brain, Shield, ArrowLeft
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LessonsPage() {
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const fetchLessons = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('[LESSONS] Fetching from:', `${BACKEND_URL}/api/lessons`);
        const res = await fetch(`${BACKEND_URL}/api/lessons`, { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (!res.ok) {
          throw new Error(`Failed to fetch lessons: ${res.status}`);
        }
        
        const data = await res.json();
        console.log('[LESSONS] Fetched:', data.length, 'lessons');
        setLessons(data);
        
        if (lessonId) {
          const lessonRes = await fetch(`${BACKEND_URL}/api/lessons/${lessonId}`, { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });
          if (lessonRes.ok) {
            const lessonData = await lessonRes.json();
            setSelectedLesson(lessonData);
            console.log('[LESSONS] Loaded lesson:', lessonData.title);
          }
        }
      } catch (error) {
        console.error('[LESSONS] Error:', error);
        setError(error.message);
        
        // Auto-retry up to 2 times after 1 second
        if (retryCount < 2) {
          console.log('[LESSONS] Retrying in 1 second... (attempt', retryCount + 1, 'of 2)');
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 1000);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchLessons();
  }, [lessonId, retryCount]);

  // Lesson Detail View
  if (lessonId && selectedLesson) {
    const difficultyConfig = {
      'Beginner': { color: 'blue', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
      'Intermediate': { color: 'yellow', bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/30' },
      'Advanced': { color: 'green', bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30' }
    };
    const config = difficultyConfig[selectedLesson.difficulty] || difficultyConfig['Beginner'];

    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
          {/* Header */}
          <div className="bg-black/40 border-b border-zinc-800 backdrop-blur-sm sticky top-0 z-10">
            <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
              <Link to="/lessons" className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2">
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back to Lessons
              </Link>
              <span className={`px-4 py-1.5 ${config.bg} ${config.text} rounded-full text-sm font-semibold`}>
                {selectedLesson.difficulty}
              </span>
            </div>
          </div>

          <div className="max-w-6xl mx-auto px-6 py-12">
            {/* Title Section */}
            <div className="mb-12 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl mb-6 shadow-lg shadow-yellow-500/20">
                <BookOpen className="w-10 h-10 text-black" />
              </div>
              <h1 className="text-5xl font-bold text-white mb-4">{selectedLesson.title}</h1>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto">{selectedLesson.description}</p>
            </div>

            {/* Lesson Content */}
            <div className={`bg-zinc-900/50 border ${config.border} rounded-2xl p-8 md:p-12 backdrop-blur-sm shadow-2xl`}>
              <div className="prose prose-invert prose-lg max-w-none">
                <div className="text-zinc-200 leading-relaxed whitespace-pre-wrap text-lg">
                  {selectedLesson.content}
                </div>
              </div>

              {/* Key Takeaways Box */}
              <div className="mt-12 bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Zap className="w-6 h-6 text-yellow-500" />
                  Key Takeaways
                </h3>
                <ul className="space-y-3 text-zinc-300">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Master this concept before moving to the next lesson</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Practice these scenarios in our Practice section</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>Discuss questions in the community forum</span>
                  </li>
                </ul>
              </div>

              {/* Next Lesson CTA */}
              <div className="mt-8 flex gap-4">
                <Link
                  to="/lessons"
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white py-4 rounded-lg font-semibold transition-colors text-center"
                >
                  View All Lessons
                </Link>
                <Link
                  to="/practice"
                  className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black py-4 rounded-lg font-semibold transition-colors text-center flex items-center justify-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  Practice Now
                </Link>
              </div>
            </div>
          </div>
        </div>
    );
  }

  // Lessons Library View
  // Display exactly what the API returns (no padding/fallback)
  const displayLessons = lessons;
  
  console.log(`[LESSONS] Displaying ${displayLessons.length} lessons:`, displayLessons.map(l => l.title));

  const difficultyIcons = {
    'Beginner': { icon: Target, color: 'blue', gradient: 'from-blue-600 to-blue-700' },
    'Intermediate': { icon: TrendingUp, color: 'yellow', gradient: 'from-yellow-600 to-yellow-700' },
    'Advanced': { icon: Award, color: 'green', gradient: 'from-green-600 to-green-700' }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        <Navigation />
        
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6bTAtNHYyaDJ2LTJoLTJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-40"></div>
          
          <div className="relative max-w-7xl mx-auto px-6 py-20">
            {/* Back Button */}
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8 group"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium">Back</span>
            </button>

            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full text-yellow-500 text-sm font-semibold mb-8">
                <Star className="w-4 h-4" />
                Master Texas Hold'em Strategy
              </div>
              
              <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 tracking-tight">
                Poker <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Lessons</span>
              </h1>
              
              <p className="text-xl text-zinc-400 mb-8 leading-relaxed">
                Structured learning path from fundamentals to advanced concepts. Learn at your own pace with our expert-crafted curriculum.
              </p>

              {/* Quick Stats */}
              <div className="flex items-center justify-center gap-8 text-zinc-400">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-yellow-500" />
                  <span className="text-white font-semibold">{displayLessons.length}</span> Lessons
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-500" />
                  <span className="text-white font-semibold">Self-Paced</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-yellow-500" />
                  <span className="text-white font-semibold">Expert Tested</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lessons Content */}
        <div className="max-w-6xl mx-auto px-6 pb-20">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mb-4"></div>
              <p className="text-zinc-400">Loading lessons...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-8 max-w-md mx-auto">
                <p className="text-red-400 mb-4">Error loading lessons: {error}</p>
                <button 
                  onClick={() => setRetryCount(prev => prev + 1)}
                  className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Single Unified Section - Strict 2x2 Grid */}
              <div>
                {/* Section Header */}
                <div className="mb-8">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
                      <Target className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white">Beginner Level</h2>
                      <p className="text-zinc-400">Build your foundation</p>
                    </div>
                  </div>
                </div>

                {/* Strict 2-Column Grid - EXACTLY 4 Cards */}
                <div className="grid md:grid-cols-2 gap-6">
                  {displayLessons.map((lesson, idx) => {
                    const lessonNum = idx + 1;
                    return (
                        <Link
                          key={lesson.lesson_id}
                          to={`/lessons/${lesson.lesson_id}`}
                          className="group relative bg-zinc-900 border border-zinc-800 rounded-2xl p-8 hover:border-zinc-700 hover:shadow-2xl hover:shadow-yellow-500/5 transition-all duration-300"
                          data-testid={`lesson-card-${lesson.lesson_id}`}
                        >
                          {/* Lesson Number Badge */}
                          <div className="absolute top-6 right-6 w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center text-zinc-400 font-bold text-sm">
                            {idx + 1}
                          </div>

                          {/* Icon */}
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg">
                            <BookOpen className="w-6 h-6 text-white" />
                          </div>

                          {/* Content */}
                          <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-yellow-500 transition-colors">
                            {lesson.title}
                          </h3>
                          <p className="text-zinc-400 leading-relaxed mb-6">
                            {lesson.description}
                          </p>

                          {/* Footer */}
                          <div className="flex items-center justify-between pt-6 border-t border-zinc-800">
                            <div className="flex items-center gap-4 text-sm text-zinc-500">
                              <div className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                <span>15-20 min</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-yellow-500 font-semibold group-hover:gap-3 transition-all">
                              <span className="text-sm">Start Lesson</span>
                              <ArrowRight className="w-4 h-4" />
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
              </div>
            </div>
          )}

          {/* CTA Section */}
          {lessons.length > 0 && (
            <div className="mt-20 bg-gradient-to-br from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-transparent"></div>
              <div className="relative">
                <div className="flex items-center justify-center gap-3 mb-6">
                  <Brain className="w-8 h-8 text-yellow-500" />
                  <h2 className="text-3xl font-bold text-white">Ready to Practice?</h2>
                </div>
                <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
                  Apply what you've learned with real poker scenarios. Test your decision-making skills and get instant feedback.
                </p>
                <Link
                  to="/practice"
                  className="inline-flex items-center gap-3 bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/40"
                >
                  <Play className="w-6 h-6" />
                  Practice Hands
                  <ArrowRight className="w-6 h-6" />
                </Link>
              </div>
            </div>
          )}

          {/* Empty State */}
          {lessons.length === 0 && !loading && (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-zinc-900 border border-zinc-800 rounded-2xl mb-6">
                <BookOpen className="w-12 h-12 text-zinc-600" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">No Lessons Yet</h2>
              <p className="text-zinc-400 mb-8">Check back soon for new content!</p>
            </div>
          )}
        </div>
      </div>
  );
}
